import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';

interface CompactMetronomeProps {
  className?: string;
  onStop?: () => void;
}

export interface CompactMetronomeRef {
  setTempo: (tempo: number) => void;
  startMetronome: () => void;
  stopMetronome: () => void;
}

const CompactMetronome = forwardRef<CompactMetronomeRef, CompactMetronomeProps>(({ 
  className = '',
  onStop
}, ref) => {
  // State
  const [tempo, setTempo] = useState(() => {
    const saved = localStorage.getItem('metronome-tempo');
    return saved ? parseInt(saved, 10) : 120;
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const nextBeatTimeRef = useRef<number>(0);
  const timeoutIdRef = useRef<number | null>(null);
  const isPlayingRef = useRef(false);
  const tempoInputRef = useRef<HTMLInputElement>(null);
  const scheduledBeatsRef = useRef<Set<number>>(new Set());

  // Save tempo to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('metronome-tempo', tempo.toString());
  }, [tempo]);

  // Update input value when tempo changes externally (e.g., from slider)
  useEffect(() => {
    if (tempoInputRef.current) {
      tempoInputRef.current.value = tempo.toString();
    }
  }, [tempo]);

  // Create a high-quality percussive click sound
  const createClickSound = useCallback((audioContext: AudioContext) => {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(1500, audioContext.currentTime);
    oscillator.type = 'sine';
    
    // Percussive envelope: quick attack, fast decay
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.001);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.05);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.05);
  }, []);

  // Schedule multiple beats ahead using Web Audio API for precise timing
  const scheduleBeats = useCallback(() => {
    if (!isPlayingRef.current || !audioContextRef.current) return;

    const audioContext = audioContextRef.current;
    const intervalSeconds = 60 / tempo;
    const currentTime = audioContext.currentTime;
    const lookahead = 0.1; // Schedule 100ms ahead
    const scheduleAhead = 0.25; // Schedule 250ms of beats ahead
    
    // Schedule beats up to scheduleAhead seconds in the future
    while (nextBeatTimeRef.current < currentTime + scheduleAhead) {
      const beatTime = nextBeatTimeRef.current;
      
      // Only schedule if we haven't already scheduled this beat
      if (!scheduledBeatsRef.current.has(beatTime)) {
        scheduledBeatsRef.current.add(beatTime);
        
        // Schedule the click sound at the precise time
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(1500, beatTime);
        oscillator.type = 'sine';
        
        // Percussive envelope
        gainNode.gain.setValueAtTime(0, beatTime);
        gainNode.gain.linearRampToValueAtTime(0.3, beatTime + 0.001);
        gainNode.gain.exponentialRampToValueAtTime(0.01, beatTime + 0.05);
        
        oscillator.start(beatTime);
        oscillator.stop(beatTime + 0.05);
      }
      
      // Move to next beat
      nextBeatTimeRef.current += intervalSeconds;
    }
    
    // Schedule the next batch of beats
    timeoutIdRef.current = window.setTimeout(scheduleBeats, lookahead * 1000);
  }, [tempo]);

  // Start metronome
  const startMetronome = useCallback(async () => {
    try {
      // Create or resume audio context
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      
      const audioContext = audioContextRef.current;
      
      // Resume if suspended (required for user interaction)
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      
      // Set up timing
      isPlayingRef.current = true;
      setIsPlaying(true);
      
      // Clear any previous scheduled beats
      scheduledBeatsRef.current.clear();
      
      // Initialize next beat time
      nextBeatTimeRef.current = audioContext.currentTime + (60 / tempo);
      
      // Start scheduling beats
      scheduleBeats();
      
    } catch (error) {
      console.error('Error starting metronome:', error);
    }
  }, [tempo, scheduleBeats]);

  // Stop metronome
  const stopMetronome = useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    
    // Clear scheduled beats
    scheduledBeatsRef.current.clear();
  }, []);

  // Toggle metronome
  const toggleMetronome = useCallback(() => {
    if (isPlaying) {
      stopMetronome();
    } else {
      startMetronome();
    }
  }, [isPlaying, startMetronome, stopMetronome]);

  // Handle tempo changes
  const handleTempoChange = useCallback((newTempo: number) => {
    const clampedTempo = Math.max(30, Math.min(240, newTempo));
    setTempo(clampedTempo);
    
    // If playing, restart with new tempo
    if (isPlayingRef.current) {
      stopMetronome();
      setTimeout(() => startMetronome(), 50);
    }
  }, [startMetronome, stopMetronome]);

  // Stable tempo setter for external control
  const setTempoExternal = useCallback((newTempo: number) => {
    handleTempoChange(newTempo);
  }, [handleTempoChange]);

  // Handle tempo input blur (when user finishes typing)
  const handleTempoInputBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const newTempo = parseInt(value, 10);
    if (!isNaN(newTempo) && newTempo >= 30 && newTempo <= 240) {
      handleTempoChange(newTempo);
    } else {
      // Reset to current tempo if invalid
      e.target.value = tempo.toString();
    }
  }, [handleTempoChange, tempo]);

  // Handle tempo input click to select all
  const handleTempoInputClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.select();
  }, []);

  // Handle tempo input focus to select all
  const handleTempoInputFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select();
  }, []);

  // Handle Enter key to apply changes
  const handleTempoKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur(); // This will trigger the blur handler
    }
  }, []);

  // Stop metronome when component unmounts or onStop is called
  useEffect(() => {
    return () => {
      stopMetronome();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopMetronome]);

  // Call onStop when metronome stops
  useEffect(() => {
    if (!isPlaying && onStop) {
      onStop();
    }
  }, [isPlaying]); // Remove onStop from dependencies to prevent re-creation

  // Expose metronome controls via ref
  useImperativeHandle(ref, () => {
    return {
      setTempo: setTempoExternal,
      startMetronome: () => {
        // Always stop first to prevent multiple overlapping timers
        stopMetronome();
        // Small delay to ensure cleanup before starting
        setTimeout(() => {
          if (!isPlayingRef.current) {
            startMetronome();
          }
        }, 10);
      },
      stopMetronome: () => {
        if (isPlayingRef.current) {
          stopMetronome();
        }
      }
    };
  }, [setTempoExternal, startMetronome, stopMetronome]);

  return (
    <div className={`relative ${className}`}>
      {/* Compact Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium transition-all duration-200 ${
          isPlaying 
            ? 'bg-red-100 text-red-700 border border-red-300 hover:bg-red-200' 
            : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
        }`}
        title="Metronome"
      >
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
        <span>{tempo}</span>
      </button>

      {/* Expanded Metronome Panel - Positioned Above */}
      {isExpanded && (
        <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-300 rounded-lg shadow-lg p-3 w-64 z-50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-800">Metronome</h4>
            <button
              onClick={() => setIsExpanded(false)}
              className="text-gray-500 hover:text-gray-700"
              title="Close"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Tempo Display and Play/Stop */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-center">
              <div className="text-xl font-mono font-bold text-gray-800">
                {tempo}
              </div>
              <div className="text-xs text-gray-500">BPM</div>
            </div>
            <button
              onClick={toggleMetronome}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                isPlaying
                  ? 'bg-red-500 hover:bg-red-600 text-white shadow-lg'
                  : 'bg-green-500 hover:bg-green-600 text-white shadow-lg'
              }`}
            >
              {isPlaying ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
              ) : (
                <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>
          </div>

          {/* Tempo Controls */}
          <div className="space-y-2">
            {/* Tempo Slider */}
            <div className="px-1">
              <input
                type="range"
                min="30"
                max="240"
                value={tempo}
                onChange={(e) => handleTempoChange(parseInt(e.target.value, 10))}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>30</span>
                <span>240</span>
              </div>
            </div>

            {/* Numeric Input */}
            <div className="flex items-center justify-center space-x-2">
              <label className="text-gray-700 text-xs">Tempo:</label>
              <input
                ref={tempoInputRef}
                type="number"
                min="30"
                max="240"
                defaultValue={tempo}
                onBlur={handleTempoInputBlur}
                onClick={handleTempoInputClick}
                onFocus={handleTempoInputFocus}
                onKeyDown={handleTempoKeyDown}
                className="w-12 px-1 py-0.5 text-center bg-gray-100 text-gray-800 rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 text-xs"
              />
              <span className="text-gray-700 text-xs">BPM</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

CompactMetronome.displayName = 'CompactMetronome';

export default CompactMetronome;
